/**
 * AI-OPS-001: `/internal/admin/api/v1/ai/**` — operator AI pricing and
 * monitoring. Amounts arrive as integer/decimal strings and stay strings.
 */
import { apiGet, apiPost, ApiError } from './apiClient.js';
import { OperatorAuthError } from './operatorApi.js';

const BASE = '/internal/admin/api/v1/ai';

async function call<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      throw new OperatorAuthError();
    }
    throw error;
  }
}

/** Persian server message when there is one. */
export function operatorAiErrorText(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.message.trim() !== '') return error.message;
  return fallback;
}

export interface AiPriceLine {
  readonly meterCode: string;
  readonly perUnits: string;
  readonly costToman: string;
  readonly customerToman: string;
}

export interface AiExampleCost {
  readonly inputTokens: string;
  readonly outputTokens: string;
  readonly toolCalls: string;
  readonly customerToman: string;
  readonly tokenCustomerToman: string;
  readonly runFeeCustomerToman: string;
  readonly providerListCostToman: string;
  readonly providerFeeToman: string;
  readonly providerCostToman: string;
  readonly providerFeeBps: number;
  readonly costTomanPerUsd: string;
  readonly grossMarginToman: string;
  readonly marginPercent: string;
  readonly tokenMarginPercent: string;
}

export interface AiCommercialPolicy {
  readonly policyVersion: number;
  readonly fxBufferBps: number;
  readonly runFeeToman: string;
  readonly defaultMicroCreditsPerToman: string;
  readonly reserveOutputTokens: number;
  readonly fxMaxAgeHours: number;
  readonly allowTrialTenants: boolean;
  readonly platformEnabled: boolean;
  readonly providerFeeBps: number;
  readonly providerTomanPerUsd: string | null;
  readonly minMarginBps: number;
  readonly reason: string;
  readonly actorId: string;
  readonly createdAt: string;
}

export interface AiPublication {
  readonly publicationVersion: number;
  readonly policyVersion: number;
  readonly providerCode: string;
  readonly modelId: string;
  readonly tomanPerUsd: string;
  readonly fxBufferBps: number;
  readonly markupNumerator: string;
  readonly markupDenominator: string;
  readonly runFeeToman: string;
  readonly reserveOutputTokens: number;
  readonly maxContextTokens: number;
  readonly maxOutputTokens: number;
  readonly defaultMicroCreditsPerToman: string;
  readonly providerFeeBps: number;
  readonly costTomanPerUsd: string;
  readonly costFxSource: 'provider_billing' | 'market';
  readonly minMarginBps: number;
  readonly tokenMarginBps: number;
  readonly reason: string;
  readonly actorId: string;
  readonly effectiveFrom: string;
  readonly priceSheet: readonly AiPriceLine[];
  readonly example: AiExampleCost;
}

export interface AiPricingOverview {
  readonly policy: AiCommercialPolicy | null;
  readonly providerSetting: {
    readonly settingVersion: number;
    readonly providerCode: string;
    readonly chatModelId: string;
    readonly maxContextTokens: number;
    readonly maxOutputTokens: number;
    readonly sourceLabel: string;
    readonly effectiveFrom: string;
  } | null;
  readonly observedRate: {
    readonly rateVersion: number;
    readonly providerCode: string;
    readonly modelId: string;
    readonly sourceLabel: string;
    readonly observedAt: string;
    readonly meters: readonly { readonly meterCode: string; readonly microUsdPerMillion: string }[];
  } | null;
  readonly markup: {
    readonly settingId: string;
    readonly numerator: string;
    readonly denominator: string;
    readonly effectiveFrom: string;
  } | null;
  readonly fx: {
    readonly observationId: string;
    readonly tomanPerUsd: string;
    readonly fetchedAt: string;
    readonly sourceUrl: string;
    readonly ageHours: number;
  } | null;
  readonly publication: AiPublication | null;
  readonly preview: {
    readonly priceSheet: readonly AiPriceLine[];
    readonly example: AiExampleCost;
    readonly tokenMarginBps: number;
  } | null;
  readonly publicationOutdated: boolean;
  readonly issues: readonly string[];
  readonly providerRuntime: {
    readonly configured: boolean;
    readonly killSwitch: boolean;
    readonly envKillSwitch: boolean;
    readonly endpoints: readonly {
      readonly providerCode: string;
      readonly enabled: boolean;
      readonly models: readonly string[];
      readonly limits?: readonly {
        readonly model: string;
        readonly maxContextTokens: number | null;
        readonly maxOutputTokens: number | null;
      }[];
    }[];
  };
}

export interface AiSyncSummary {
  readonly publicationVersion: number | null;
  readonly processed: number;
  readonly applied: number;
  readonly current: number;
  readonly failed: readonly { readonly slug: string; readonly error: string }[];
}

export interface AiTenantUsage {
  readonly slug: string;
  readonly displayName: string;
  readonly optedIn: boolean;
  readonly killSwitch: boolean;
  readonly modelCode: string | null;
  readonly microCreditsPerToman: string | null;
  readonly balanceToman: string;
  readonly reservedToman: string;
  readonly runs: number;
  readonly settledRuns: number;
  readonly failedRuns: number;
  readonly pendingRuns: number;
  readonly unknownUsageRuns: number;
  readonly inputTokens: string;
  readonly outputTokens: string;
  readonly revenueToman: string;
  readonly providerCostToman: string;
  readonly grossMarginToman: string;
  readonly purchasesToman: string;
  readonly welcomeToman: string;
  readonly sponsoredImportRuns: number;
  readonly lastRunAt: string | null;
}

export interface AiUsageReport {
  readonly windowDays: number;
  readonly fx: AiPricingOverview['fx'];
  readonly totals: {
    readonly tenants: number;
    readonly optedInTenants: number;
    readonly runs: number;
    readonly settledRuns: number;
    readonly failedRuns: number;
    readonly pendingRuns: number;
    readonly unknownUsageRuns: number;
    readonly inputTokens: string;
    readonly outputTokens: string;
    readonly revenueToman: string;
    readonly providerCostToman: string;
    readonly grossMarginToman: string;
    readonly marginPercent: string;
    readonly purchasesToman: string;
    readonly outstandingBalanceToman: string;
    readonly welcomeToman: string;
  };
  readonly byTenant: readonly AiTenantUsage[];
  readonly byTool: readonly {
    readonly tool: string;
    readonly runs: number;
    readonly settled: number;
    readonly revenueToman: string;
    readonly providerCostToman: string;
    readonly grossMarginToman: string;
  }[];
  readonly daily: readonly {
    readonly day: string;
    readonly runs: number;
    readonly revenueToman: string;
    readonly providerCostToman: string;
  }[];
  readonly recentRuns: readonly {
    readonly slug: string;
    readonly runId: string;
    readonly tool: string;
    readonly assistantKey?: string | null;
    readonly actorId?: string | null;
    readonly actorName?: string | null;
    readonly modelCode?: string | null;
    readonly providerCode?: string | null;
    readonly question?: string | null;
    readonly answer?: string | null;
    readonly citations?: readonly string[];
    readonly createdAt: string;
    readonly executionStatus: string;
    readonly billingStatus: string;
    readonly inputTokens: string | null;
    readonly outputTokens: string | null;
    readonly cachedInputTokens?: string | null;
    readonly reasoningTokens?: string | null;
    readonly chargeToman: string;
    readonly providerCostToman: string;
  }[];
  readonly failures: readonly { readonly slug: string; readonly error: string }[];
}

export const getAiPricing = () => call(() => apiGet<AiPricingOverview>(`${BASE}/pricing`));

export const listAiPublications = () =>
  call(
    async () =>
      (await apiGet<{ publications: readonly AiPublication[] }>(`${BASE}/pricing/publications`))
        .publications,
  );

export const saveAiPolicy = (input: {
  readonly fxBufferBps: number;
  readonly runFeeToman: string;
  readonly defaultMicroCreditsPerToman: string;
  readonly reserveOutputTokens: number;
  readonly fxMaxAgeHours: number;
  readonly allowTrialTenants: boolean;
  readonly platformEnabled: boolean;
  readonly providerFeeBps: number;
  /** Empty string or null: the market FX is the cost basis. */
  readonly providerTomanPerUsd: string | null;
  readonly minMarginBps: number;
  readonly reason: string;
}) => call(() => apiPost<AiCommercialPolicy>(`${BASE}/pricing/policy`, input));

export const recordAiFx = (tomanPerUsd: string) =>
  call(() => apiPost(`${BASE}/pricing/fx`, { tomanPerUsd }));

export const refreshAiFx = () => call(() => apiPost(`${BASE}/pricing/fx/refresh`, {}));

export const setAiMarkup = (numerator: string, denominator: string, reason: string) =>
  call(() => apiPost(`${BASE}/pricing/markup`, { numerator, denominator, reason }));

export const setAiModelRate = (input: {
  readonly providerCode: string;
  readonly modelId: string;
  readonly maxContextTokens: number;
  readonly maxOutputTokens: number;
  readonly inputMicroUsdPerMillion: string;
  readonly cachedInputMicroUsdPerMillion: string;
  readonly outputMicroUsdPerMillion: string;
  readonly sourceLabel: string;
}) => call(() => apiPost(`${BASE}/pricing/model-rate`, input));

export const publishAiPricing = (reason: string) =>
  call(() =>
    apiPost<{ publication: AiPublication; sync: AiSyncSummary }>(`${BASE}/pricing/publish`, {
      reason,
    }),
  );

export const syncAiTenants = () => call(() => apiPost<AiSyncSummary>(`${BASE}/pricing/sync`, {}));

export interface AiFinanceFigures {
  readonly runs: number;
  readonly settledRuns: number;
  readonly unknownRuns: number;
  readonly releasedRuns: number;
  readonly pendingRuns: number;
  readonly snapshotRuns: number;
  readonly estimatedCostRuns: number;
  readonly paidOrders: number;
  readonly cashSalesToman: string;
  readonly gatewayFeeToman: string;
  readonly usageValueToman: string;
  readonly paidUsageRevenueToman: string;
  readonly promotionalUsageToman: string;
  readonly tokenRevenueToman: string;
  readonly runFeeRevenueToman: string;
  readonly billableReads: string;
  readonly providerListCostToman: string;
  readonly providerFeeToman: string;
  readonly providerCostToman: string;
  readonly unbilledProviderCostToman: string;
  readonly grossMarginToman: string;
  readonly grossMarginPercent: string;
  readonly usageMarginToman: string;
  readonly usageMarginPercent: string;
  readonly tokenMarginToman: string;
  readonly tokenMarginPercent: string;
  readonly unusedPaidCreditToman: string;
  readonly unusedPromotionalCreditToman: string;
}

export interface AiUsageMetric {
  readonly samples: number;
  readonly p50: string | null;
  readonly p90: string | null;
  readonly p99: string | null;
  readonly mean: string | null;
}

export interface AiFinanceReport {
  readonly windowDays: number;
  readonly generatedAt: string;
  readonly totals: AiFinanceFigures;
  readonly metrics: {
    readonly measuredRuns: number;
    readonly inputTokens: AiUsageMetric;
    readonly cachedInputTokens: AiUsageMetric;
    readonly outputTokens: AiUsageMetric;
    readonly reasoningTokens: AiUsageMetric;
    readonly weightedTokens: AiUsageMetric;
    readonly providerCostToman: AiUsageMetric;
    readonly chargeToman: AiUsageMetric;
    readonly cachedInputPercent: string;
    readonly reasoningOfOutputPercent: string;
    readonly reasoningReportedRuns: number;
    readonly outputCapReachedRuns: number;
  };
  readonly byTenant: readonly (AiFinanceFigures & {
    readonly slug: string;
    readonly displayName: string;
  })[];
  readonly byDay: readonly {
    readonly day: string;
    readonly runs: number;
    readonly cashSalesToman: string;
    readonly paidUsageRevenueToman: string;
    readonly usageValueToman: string;
    readonly providerCostToman: string;
    readonly grossMarginToman: string;
  }[];
  readonly failures: readonly { readonly slug: string; readonly error: string }[];
}

export const getAiFinance = (days: number) =>
  call(() => apiGet<AiFinanceReport>(`${BASE}/finance?days=${encodeURIComponent(String(days))}`));

export const getAiUsage = (days: number) =>
  call(() => apiGet<AiUsageReport>(`${BASE}/usage?days=${encodeURIComponent(String(days))}`));

export const setAiTenantKillSwitch = (slug: string, killed: boolean, reason: string) =>
  call(() =>
    apiPost(`${BASE}/tenants/${encodeURIComponent(slug)}/kill-switch`, { killed, reason }),
  );

export const grantAiTenantCredit = (slug: string, amountToman: string, reason: string) =>
  call(() =>
    apiPost<{ operationId: string; replayed: boolean; amountMicroCredits: string }>(
      `${BASE}/tenants/${encodeURIComponent(slug)}/grant`,
      { amountToman, reason, idempotencyKey: crypto.randomUUID() },
    ),
  );

export interface OperatorModelProfile {
  readonly modelId: string;
  readonly modelCode: string;
  readonly displayName: string;
  readonly providerCode: string;
  readonly tier: 'fast' | 'standard' | 'advanced' | 'embedding';
  readonly maxContextTokens: number;
  readonly maxOutputTokens: number;
  readonly supportsTools: boolean;
  readonly supportsVision: boolean;
  readonly descriptionFa: string;
}

export interface TenantModelConfig {
  readonly slug: string;
  readonly displayName: string;
  readonly optedIn: boolean;
  readonly killSwitch: boolean;
  readonly defaultModelCode: string | null;
  readonly defaultProviderCode: string | null;
  readonly yarModels: Record<string, string>;
  readonly yarPrompts?: Record<string, string>;
  readonly defaultPrompts?: Record<
    string,
    { readonly role: string; readonly rules: string; readonly header: string }
  >;
  readonly availableModels: readonly OperatorModelProfile[];
}

export interface AiRunDetail {
  readonly slug: string;
  readonly tenantName: string;
  readonly runId: string;
  readonly actorId: string | null;
  readonly actorName: string | null;
  readonly tool: string | null;
  readonly assistantKey: string;
  readonly modelCode: string | null;
  readonly providerCode: string | null;
  readonly question: string | null;
  readonly answer: string | null;
  readonly evidence: readonly unknown[];
  readonly citations: readonly string[];
  readonly executionStatus: string;
  readonly billingStatus: string;
  readonly tokens: {
    readonly inputTokens: string;
    readonly outputTokens: string;
    readonly cachedInputTokens: string | null;
    readonly reasoningTokens: string | null;
  };
  readonly chargeToman: string;
  readonly providerCostToman: string;
  readonly consumedMicroCredits: string;
  readonly createdAt: string;
}

export const getAiModels = () =>
  call(
    async () =>
      (await apiGet<{ models: readonly OperatorModelProfile[] }>(`${BASE}/models`)).models,
  );

export const getTenantAiModels = (slug: string) =>
  call(() => apiGet<TenantModelConfig>(`${BASE}/tenants/${encodeURIComponent(slug)}/models`));

export const setTenantAiModels = (
  slug: string,
  input: {
    readonly defaultModelCode?: string | null;
    readonly yarModels?: Record<string, string>;
    readonly yarPrompts?: Record<string, string>;
    readonly reason: string;
  },
) =>
  call(() =>
    apiPost<{ saved: boolean }>(`${BASE}/tenants/${encodeURIComponent(slug)}/models`, input),
  );

export const getAiRunDetail = (slug: string, runId: string) =>
  call(() =>
    apiGet<AiRunDetail>(
      `${BASE}/tenants/${encodeURIComponent(slug)}/runs/${encodeURIComponent(runId)}`,
    ),
  );

export interface AiQualityReport {
  readonly windowDays: number;
  readonly totalRuns: number;
  readonly successfulRuns: number;
  readonly failedRuns: number;
  readonly errorRatePercent: string;
  readonly unknownUsageRuns: number;
  readonly groundedRuns: number;
  readonly groundingRatePercent: string;
  readonly failuresByError: readonly { readonly error: string; readonly count: number }[];
  readonly recentFailures: readonly {
    readonly runId: string;
    readonly slug: string;
    readonly tool: string;
    readonly error: string;
    readonly createdAt: string;
  }[];
  readonly feedbackSummary: {
    readonly totalFeedback: number;
    readonly positiveFeedback: number;
    readonly negativeFeedback: number;
    readonly satisfactionPercent: string;
  };
}

export const getAiQuality = (days?: number) =>
  call(() =>
    apiGet<AiQualityReport>(
      days === undefined ? `${BASE}/quality` : `${BASE}/quality?days=${encodeURIComponent(days)}`,
    ),
  );
